/**
 * TanStack Query hook for the profile-aware LLM compare-reasoning narrative
 * (COMPARE_REASONING_V1).
 *
 * Keyed (dealId, profile) — switching scoring profile produces a distinct
 * cache entry, so the query key MUST include the profile. Returns undefined
 * while loading or on 404; the verdict-first /compare view treats undefined /
 * 404 / is_fallback === true as a signal to render deterministic copy.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../client';
import type { CompareReasoningResponse } from '../types';

export const compareReasoningKeys = {
  all: ['compareReasoning'] as const,
  deal: (dealId: number, profile: string) =>
    [...compareReasoningKeys.all, dealId, profile] as const,
};

export function useCompareReasoning(
  dealId: number | null | undefined,
  profile: string,
  options?: { enabled?: boolean },
) {
  const { enabled = true } = options ?? {};
  return useQuery<CompareReasoningResponse>({
    queryKey: compareReasoningKeys.deal(dealId ?? 0, profile),
    queryFn: () => api.compareReasoning.get(dealId as number, profile),
    enabled: enabled && !!dealId && !!profile,
    // Compare narrative regenerates on a slow cycle; safe to cache for a few
    // minutes so profile toggles feel instant after the first fetch.
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      // 404 = narrative not generated yet (deal has < 2 ANALYZED sheets, or
      // the worker hasn't generated this profile yet). Don't hammer it; the
      // next route navigation or profile switch will retry naturally.
      if (status === 404) {return false;}
      return failureCount < 2;
    },
  });
}
